import React, { useEffect, useMemo, useState } from "react";
import { executeQuery } from "../../../graphql/client";

// Interests tab (#276): who said they're into which event types. Reads
// the "interested in" answer from the /join form via the admin-only
// eventInterests query and answers one question: "give me everyone
// interested in these types (any / all of them), newest first" — e.g.
// everybody who ticked volleyball, to size and announce a league.
// Read-only; the Copy emails button is the only output.

const STATUS_LABELS = {
  lead: "lead",
  account: "account",
  approved: "approved",
  paid: "paid 💳",
};

const STATUS_FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "paid", label: "Paying" },
  { id: "approved", label: "Approved +" },
  { id: "lead", label: "Leads only" },
];

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const titleCase = (s) =>
  String(s || "")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

const pill = (active, small) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: small ? "4px 10px" : "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const count = {
  background: "#eef1f5",
  borderRadius: 999,
  padding: "0 7px",
  fontSize: 12,
  fontWeight: 700,
  color: "#4a5263",
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};

const muted = { color: "#6a7385", fontSize: 13 };

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };

const Stat = ({ label, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 140px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const statusRank = { lead: 0, account: 1, approved: 2, paid: 3 };

const InterestsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [matchAll, setMatchAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFreeText, setShowFreeText] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(`query EventInterests { eventInterests }`);
      setData(out?.eventInterests ? JSON.parse(out.eventInterests) : null);
    } catch (err) {
      setError(err.message || "Couldn't load interests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const types = data?.types || [];
  const people = data?.people || [];
  const totals = data?.totals || { rows: 0, withInterests: 0, paidWithInterests: 0 };

  const knownTypes = useMemo(() => types.filter((t) => t.known), [types]);
  const freeTextTypes = useMemo(() => types.filter((t) => !t.known), [types]);

  const toggle = (type) =>
    setSelected((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );

  const filtered = useMemo(() => {
    const minRank =
      statusFilter === "paid" ? 3 : statusFilter === "approved" ? 2 : 0;
    return people.filter((p) => {
      if (statusFilter === "lead" && p.status !== "lead") return false;
      if (statusRank[p.status] < minRank) return false;
      if (!selected.length) return true;
      return matchAll
        ? selected.every((t) => p.interests.includes(t))
        : selected.some((t) => p.interests.includes(t));
    });
  }, [people, selected, matchAll, statusFilter]);

  const emails = useMemo(
    () => [...new Set(filtered.map((p) => p.email).filter(Boolean))],
    [filtered]
  );

  const copyEmails = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      setCopied(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
    } catch (err) {
      setCopied("Couldn't copy — select the list below instead");
    }
    setTimeout(() => setCopied(""), 2500);
  };

  const selectionLabel = selected.length
    ? selected.map(titleCase).join(matchAll ? " + " : " or ")
    : "any interest";

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="target">🎯</span> Event Interests
      </h2>
      <p style={muted}>
        Who said they're into what, from the "What are you interested in?"
        checkboxes on /join. Pick one or more types to get everyone who ticked
        them, newest signup first, then copy their emails. Rows that never
        answered aren't listed.
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {loading && !data && <p style={muted}>Loading…</p>}

      {data && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat
              label="Told us an interest"
              value={totals.withInterests}
              sub={`of ${totals.rows} people on file`}
            />
            <Stat
              label="Paying, with interests"
              value={totals.paidWithInterests}
              sub="members you can plan around"
            />
            <Stat
              label="Matching now"
              value={filtered.length}
              sub={selectionLabel}
            />
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Event types</div>
            <div style={{ ...muted, marginBottom: 10 }}>
              Count is everyone who ticked it; the second number is how many of
              them are paying. Tap to filter.
            </div>
            {knownTypes.length === 0 ? (
              <p style={muted}>Nobody has answered the interests question yet.</p>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {knownTypes.map((t) => (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => toggle(t.type)}
                    style={pill(selected.includes(t.type))}
                    title={`${t.count} interested · ${t.paid} paying · latest ${fmtDate(t.latestAt)}`}
                  >
                    {titleCase(t.type)}
                    <span style={count}>
                      {t.count}
                      <span style={{ fontWeight: 500, color: "#8a93a3" }}> · {t.paid}💳</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {freeTextTypes.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowFreeText((v) => !v)}
                  style={{ ...pill(false, true), background: "#f6f7fa" }}
                >
                  {showFreeText ? "Hide" : "Show"} {freeTextTypes.length} typed-in
                  answer{freeTextTypes.length === 1 ? "" : "s"}
                </button>
                {showFreeText && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {freeTextTypes.map((t) => (
                      <button
                        key={t.type}
                        type="button"
                        onClick={() => toggle(t.type)}
                        style={pill(selected.includes(t.type), true)}
                      >
                        {t.type}
                        <span style={count}>{t.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid #eef1f5",
              }}
            >
              <span style={{ ...muted, fontWeight: 600 }}>Match</span>
              <button type="button" onClick={() => setMatchAll(false)} style={pill(!matchAll, true)}>
                any selected
              </button>
              <button type="button" onClick={() => setMatchAll(true)} style={pill(matchAll, true)}>
                all selected
              </button>
              <span style={{ ...muted, fontWeight: 600, marginLeft: 10 }}>Status</span>
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatusFilter(f.id)}
                  style={pill(statusFilter === f.id, true)}
                >
                  {f.label}
                </button>
              ))}
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  style={{ ...pill(false, true), background: "#f6f7fa", marginLeft: "auto" }}
                >
                  Clear types
                </button>
              )}
            </div>
          </div>

          <div style={card}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <div style={{ fontWeight: 700 }}>
                People · {filtered.length}
                <span style={{ ...muted, fontWeight: 500 }}> · {selectionLabel}</span>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                {copied && <span style={muted}>{copied}</span>}
                <button
                  type="button"
                  onClick={copyEmails}
                  disabled={!emails.length}
                  style={{ ...pill(false, true), opacity: emails.length ? 1 : 0.5 }}
                >
                  Copy {emails.length} email{emails.length === 1 ? "" : "s"}
                </button>
                <button
                  type="button"
                  onClick={load}
                  disabled={loading}
                  style={{ ...pill(false, true), background: "#f6f7fa" }}
                >
                  {loading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              Newest signup first. Selected types are highlighted in each
              person's list; hover a row's interests for exactly what they typed.
            </div>
            {filtered.length === 0 ? (
              <p style={muted}>Nobody matches this combination.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Name</th>
                      <th style={th}>Email</th>
                      <th style={th}>Status</th>
                      <th style={th}>Signed up</th>
                      <th style={th}>Interested in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.userId} style={{ borderBottom: "1px solid #f1f3f7" }}>
                        <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>
                          {p.name || <span style={muted}>(no name)</span>}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {p.email ? (
                            <a href={`mailto:${p.email}`} style={{ color: OCEAN }}>
                              {p.email}
                            </a>
                          ) : (
                            <span style={muted}>–</span>
                          )}
                        </td>
                        <td
                          style={{
                            ...td,
                            whiteSpace: "nowrap",
                            fontWeight: p.status === "paid" ? 700 : 400,
                            color: p.status === "lead" ? "#8a93a3" : "#1f2330",
                          }}
                        >
                          {STATUS_LABELS[p.status] || p.status}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(p.date) || "–"}</td>
                        <td style={td} title={p.raw}>
                          {p.interests.map((t) => {
                            const on = selected.includes(t);
                            return (
                              <span
                                key={t}
                                style={{
                                  display: "inline-block",
                                  margin: "0 6px 4px 0",
                                  padding: "1px 8px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  background: on ? SEAFOAM : "#f4f5f8",
                                  border: on ? `1px solid ${OCEAN}` : "1px solid transparent",
                                  fontWeight: on ? 700 : 500,
                                  color: "#333",
                                }}
                              >
                                {titleCase(t)}
                              </span>
                            );
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InterestsTab;
